import React from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Sparkles, AlertCircle } from 'lucide-react';
import QuizPlayer from './QuizPlayer';

const markdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-black text-foreground mt-5 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-foreground mt-5 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold text-foreground mt-4 mb-1.5 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-bold text-foreground/90 mt-3 mb-1 first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-foreground/90 leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1.5 mb-3 text-sm text-foreground/90">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1.5 mb-3 text-sm text-foreground/90">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-bold text-foreground">{children}</strong>,
  code: ({ children }) => <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground mb-3">{children}</blockquote>,
};

const AIResultModal = ({ isOpen, onClose, loading, error, title, textContent, questions, materialTitle }) => {
  if (!isOpen) return null;

  const isQuiz = Array.isArray(questions);
  const modalWidth = isQuiz && questions.length > 0 && !loading && !error ? 'max-w-2xl' : 'max-w-xl';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-card rounded-2xl shadow-2xl w-full ${modalWidth} max-h-[85vh] flex flex-col overflow-hidden animate-scale-in transition-[max-width]`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h3 className="section-title flex items-center gap-2 text-foreground font-bold">
            <Sparkles className="w-4 h-4 text-accent" /> {title}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center transition-colors border-none cursor-pointer bg-transparent">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent" />
              <span className="text-xs text-muted-foreground">AI is working on it...</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center text-center gap-2 py-10">
              <AlertCircle className="w-6 h-6 text-destructive" />
              <p className="text-sm text-destructive font-medium">{error}</p>
            </div>
          )}

          {!loading && !error && textContent && (
            <ReactMarkdown components={markdownComponents}>{textContent}</ReactMarkdown>
          )}

          {!loading && !error && isQuiz && questions.length > 0 && (
            <QuizPlayer questions={questions} title={title} materialTitle={materialTitle} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIResultModal;
